/** MY TAP 'N WIN — the player's own corner, one screen per thing.
 *
 *  Invite friends, my wins, my history, my friends and my reminder. Everything
 *  on these screens belongs to THIS player at THIS business and is read from
 *  one call; nothing here touches the game itself.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bell, Check, Copy, Gift, Loader2, Mail, MessageSquare, Trophy,
         UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

/** One read for the whole corner: the invite link, the friends, the wins, the
 *  history and the reminder. Re-read whenever a screen is opened. */
export const useMyHub = (cardId, open) => {
    const [hub, setHub] = useState(null);
    const [loading, setLoading] = useState(false);
    const read = useCallback(async () => {
        if (!cardId) return;
        setLoading(true);
        try {
            const device = localStorage.getItem('patron_device_id') || '';
            const res = await fetch(
                `${API}/api/goal-game/public/${cardId}/my-hub?device_id=${encodeURIComponent(device)}`);
            setHub(res.ok ? await res.json() : null);
        } catch (_e) { setHub(null); } finally { setLoading(false); }
    }, [cardId]);
    useEffect(() => { if (open) read(); }, [open, read]);
    return { hub, loading, reload: read, setHub };
};

const Panel = ({ children, c, testid }) => (
    <div className="rounded-2xl border p-5" data-testid={testid}
         style={{ backgroundColor: 'rgba(255,255,255,0.07)', borderColor: `${c.accent}33` }}>
        {children}
    </div>
);

const Row = ({ icon: Icon, title, sub, right, c, testid }) => (
    <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-black/25 px-4 py-3"
         data-testid={testid}>
        {Icon && <Icon size={16} className="shrink-0" style={{ color: c.accent }} />}
        <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold">{title}</span>
            {sub && <span className="block truncate text-[11px] opacity-70">{sub}</span>}
        </span>
        {right && <span className="shrink-0 text-[11px] font-bold opacity-80">{right}</span>}
    </div>
);

const Empty = ({ text, testid }) => (
    <p className="py-6 text-center text-sm opacity-60" data-testid={testid}>{text}</p>
);

/** INVITE FRIENDS — the personal link, to copy, to email or to text. Who used
 *  it is recorded on the new player's profile. No reward is paid for it. */
export const GoalInviteScreen = ({ hub, loading, t, c, ty, primaryBtn, ghostBtn }) => {
    const p = (k, o) => t(`goalGame.hub.${k}`, o);
    const url = hub?.invite_url || '';
    const share = p('inviteMessage', { name: hub?.screen_name || '' });
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success(p('copied'));
        } catch (_e) { toast.error(p('copyFailed')); }
    };
    return (
        <Panel c={c} testid="goal-hub-invite">
            <p className="text-2xl font-bold" style={ty.headline}
               data-testid="goal-hub-invite-title">{p('inviteTitle')}</p>
            <p className="mt-1 text-sm opacity-75">{p('inviteBody')}</p>
            {loading && <Loader2 className="mx-auto mt-5 animate-spin opacity-50" size={20} />}
            {url && (
                <>
                    <p className="mt-5 break-all rounded-xl border border-white/15 bg-black/30 px-3 py-3 text-xs"
                       data-testid="goal-hub-invite-link">{url}</p>
                    <button type="button" onClick={copy} style={primaryBtn}
                            data-testid="goal-hub-invite-copy"
                            className="mt-4 w-full min-h-[56px] rounded-xl py-4 text-lg font-bold shadow-lg">
                        <span className="inline-flex items-center gap-2"><Copy size={18} />{p('copyLink')}</span>
                    </button>
                    <a href={`mailto:?subject=${encodeURIComponent(p('inviteSubject'))}&body=${encodeURIComponent(`${share} ${url}`)}`}
                       style={ghostBtn} data-testid="goal-hub-invite-email"
                       className="mt-3 flex w-full min-h-[56px] items-center justify-center gap-2 rounded-xl border border-white/25 py-4 text-base font-bold">
                        <Mail size={18} />{p('byEmail')}
                    </a>
                    <a href={`sms:?&body=${encodeURIComponent(`${share} ${url}`)}`}
                       style={ghostBtn} data-testid="goal-hub-invite-sms"
                       className="mt-3 flex w-full min-h-[56px] items-center justify-center gap-2 rounded-xl border border-white/25 py-4 text-base font-bold">
                        <MessageSquare size={18} />{p('bySms')}
                    </a>
                    <p className="mt-4 text-center text-[11px] opacity-60"
                       data-testid="goal-hub-invite-count">
                        {p('invitedCount', { count: hub?.friends?.invited_count || 0 })}
                    </p>
                </>
            )}
        </Panel>
    );
};

/** MY WINS — every gift this player has won here. */
export const GoalWinsScreen = ({ hub, loading, t, c, ty, lang }) => {
    const p = (k, o) => t(`goalGame.hub.${k}`, o);
    const rows = hub?.wins || [];
    const when = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString(
                lang === 'fr' ? 'fr-CA' : (lang === 'es' ? 'es-MX' : 'en-CA'),
                { day: 'numeric', month: 'short', year: 'numeric' });
        } catch (_e) { return ''; }
    };
    return (
        <Panel c={c} testid="goal-hub-wins">
            <p className="text-2xl font-bold" style={ty.headline}
               data-testid="goal-hub-wins-title">{p('winsTitle')}</p>
            {loading && <Loader2 className="mx-auto mt-5 animate-spin opacity-50" size={20} />}
            {!loading && rows.length === 0 && (
                <Empty text={p('winsEmpty')} testid="goal-hub-wins-empty" />
            )}
            <div className="mt-4 space-y-2">
                {rows.map((w, i) => (
                    <Row key={i} icon={Gift} c={c} testid={`goal-hub-win-${i}`}
                         title={(lang === 'fr' && w.title_fr) ? w.title_fr : w.title}
                         sub={[when(w.won_at), w.location].filter(Boolean).join(' · ')}
                         right={w.redeemed ? p('used') : p('open')} />
                ))}
            </div>
        </Panel>
    );
};

/** MY HISTORY — the nights this player picked in. */
export const GoalHistoryScreen = ({ hub, loading, t, c, ty }) => {
    const p = (k, o) => t(`goalGame.hub.${k}`, o);
    const rows = hub?.history || [];
    return (
        <Panel c={c} testid="goal-hub-history">
            <p className="text-2xl font-bold" style={ty.headline}
               data-testid="goal-hub-history-title">{p('historyTitle')}</p>
            {loading && <Loader2 className="mx-auto mt-5 animate-spin opacity-50" size={20} />}
            {!loading && rows.length === 0 && (
                <Empty text={p('historyEmpty')} testid="goal-hub-history-empty" />
            )}
            <div className="mt-4 space-y-2">
                {rows.map((h, i) => (
                    <Row key={i} icon={Trophy} c={c} testid={`goal-hub-history-${i}`}
                         title={h.played_on || p('historyNoDate')}
                         sub={[h.team ? p(h.team === 'home' ? 'teamHome' : 'teamAway') : '',
                               h.star ? p('starWas', { name: h.star }) : '']
                             .filter(Boolean).join(' · ')}
                         right={h.right ? p('right') : ''} />
                ))}
            </div>
        </Panel>
    );
};

/** MY FRIENDS — screen names only. */
export const GoalFriendsScreen = ({ hub, loading, t, c, ty }) => {
    const p = (k, o) => t(`goalGame.hub.${k}`, o);
    const names = hub?.friends?.invited || [];
    const by = hub?.friends?.referred_by || '';
    return (
        <Panel c={c} testid="goal-hub-friends">
            <p className="text-2xl font-bold" style={ty.headline}
               data-testid="goal-hub-friends-title">{p('friendsTitle')}</p>
            {by && (
                <p className="mt-2 text-sm opacity-75" data-testid="goal-hub-referred-by">
                    {p('referredBy', { name: by })}
                </p>
            )}
            {loading && <Loader2 className="mx-auto mt-5 animate-spin opacity-50" size={20} />}
            {!loading && names.length === 0 && (
                <Empty text={p('friendsEmpty')} testid="goal-hub-friends-empty" />
            )}
            <div className="mt-4 space-y-2">
                {names.map((n, i) => (
                    <Row key={i} icon={Users} c={c} title={n} testid={`goal-hub-friend-${i}`} />
                ))}
            </div>
        </Panel>
    );
};

/** REMINDERS — on by default, 30 minutes before, by email. Off and back on in
 *  one tap, and the time is a single row of choices. */
export const GoalReminderScreen = ({ cardId, hub, loading, t, c, ty, onSaved,
                                    primaryBtn, ghostBtn }) => {
    const p = (k, o) => t(`goalGame.hub.${k}`, o);
    const [busy, setBusy] = useState(false);
    const value = hub?.reminder || { on: true, minutes: 30 };
    const save = async (patch) => {
        setBusy(true);
        try {
            const device = localStorage.getItem('patron_device_id') || '';
            const res = await fetch(`${API}/api/goal-game/public/${cardId}/my-reminder`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_id: device, ...patch }),
            });
            if (!res.ok) throw new Error('save');
            const out = await res.json();
            onSaved?.(out);
            toast.success(out.on ? p('reminderOnNow') : p('reminderOffNow'));
        } catch (_e) {
            toast.error(t('goalGame.saveErrors.offline'));
        } finally { setBusy(false); }
    };
    return (
        <Panel c={c} testid="goal-hub-reminders">
            <p className="text-2xl font-bold" style={ty.headline}
               data-testid="goal-hub-reminder-title">{p('reminderTitle')}</p>
            <p className="mt-1 text-sm opacity-75" data-testid="goal-hub-reminder-body">
                {value.on ? p('reminderOnBody', { minutes: value.minutes })
                    : p('reminderOffBody')}
            </p>
            {loading && <Loader2 className="mx-auto mt-5 animate-spin opacity-50" size={20} />}
            <button type="button" disabled={busy} onClick={() => save({ on: !value.on })}
                    style={value.on ? ghostBtn : primaryBtn}
                    data-testid="goal-hub-reminder-switch"
                    className="mt-5 w-full min-h-[56px] rounded-xl border border-white/25 py-4 text-lg font-bold">
                <span className="inline-flex items-center gap-2">
                    {busy ? <Loader2 className="animate-spin" size={18} /> : <Bell size={18} />}
                    {value.on ? p('turnOff') : p('turnOn')}
                </span>
            </button>
            {value.on && (
                <div className="mt-4 grid grid-cols-4 gap-2" data-testid="goal-hub-reminder-times">
                    {[15, 30, 60, 120].map((m) => (
                        <button key={m} type="button" disabled={busy}
                                onClick={() => save({ minutes: m })}
                                data-testid={`goal-hub-reminder-${m}`}
                                className="min-h-[48px] rounded-xl border px-1 text-xs font-bold"
                                style={Number(value.minutes) === m
                                    ? { backgroundColor: c.button, borderColor: c.button, color: '#fff' }
                                    : { borderColor: 'rgba(255,255,255,0.25)',
                                        backgroundColor: 'rgba(255,255,255,0.06)' }}>
                            {Number(value.minutes) === m && <Check size={12} className="mx-auto mb-0.5" />}
                            {p('minutesShort', { minutes: m })}
                        </button>
                    ))}
                </div>
            )}
            <p className="mt-4 text-[11px] opacity-60" data-testid="goal-hub-reminder-note">
                {p('reminderNote')}
            </p>
        </Panel>
    );
};

export const HubIcons = { invite: UserPlus, wins: Gift, history: Trophy,
                          friends: Users, reminders: Bell };
